/**
 * Google Consent Mode, in one place because two places is a bug.
 *
 * `dataLayer` and `gtag` are a single global pair shared by everything Google loads -
 * GA4 from `analytics.ts`, a Tag Manager container from `tags.ts` - and Consent Mode has
 * one rule that matters: **the defaults must be pushed before any Google tag loads.** A
 * second `consent default` after a container is running is ignored, so if each caller
 * pushed its own the second one would silently do nothing and the order would depend on
 * which tag the CMS listed first. This module owns both, and pushes the defaults exactly
 * once.
 *
 * ## What the signals mean here
 *
 * Google's storage types are mapped onto the two categories this site asks about:
 *
 * | Google | ours |
 * |---|---|
 * | `analytics_storage` | Analytics |
 * | `ad_storage`, `ad_user_data`, `ad_personalization` | Marketing |
 * | `functionality_storage`, `security_storage` | granted - the site cannot work without them |
 *
 * `ads_data_redaction` is set while marketing is denied: it strips identifiers from the
 * pings Google still sends in denied mode, and is the difference between "denied" and
 * "denied and not quietly collecting anyway".
 *
 * `wait_for_update` gives a tag that loaded before the answer half a second to hear it,
 * which only matters for a container running in always-on mode - see `tags.ts`.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface ConsentGrants {
  analytics: boolean;
  marketing: boolean;
}

let defaultsPushed = false;

/**
 * Pushes the `arguments` object, not an array.
 *
 * Google's own snippet is `function gtag(){dataLayer.push(arguments)}` and their docs are
 * written against that shape. The previous implementation here pushed a real array, which
 * worked for the `config` and `event` commands it was verified with against a live
 * property - so this is not a bug being fixed. It is the `consent` command, which arrives
 * before any tag has loaded and cannot be checked by looking at a report afterwards, not
 * being the place to find out whether the difference matters.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-rest-params
export function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/** Everything denied. Idempotent, and must run before any Google tag is inserted. */
export function pushConsentDefaults() {
  if (defaultsPushed) return;
  defaultsPushed = true;

  if (!window.gtag) window.gtag = gtag;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500
  });
  gtag('set', 'ads_data_redaction', true);
  gtag('set', 'url_passthrough', true);
}

/** The visitor's decision, in Google's vocabulary. Safe to call repeatedly. */
export function updateConsent({ analytics, marketing }: ConsentGrants) {
  pushConsentDefaults();

  gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied'
  });
  gtag('set', 'ads_data_redaction', !marketing);
}

/** Test seam: what has been pushed, in order. */
export const consentModeState = () => ({ defaultsPushed, entries: (window.dataLayer || []).length });
